import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthProvider";
import { useClickOutside } from "@/hooks/useClickOutside";

function profileInitial(email?: string | null): string {
  const trimmed = email?.trim();
  if (!trimmed) return "?";
  return trimmed.charAt(0).toUpperCase();
}

/** Профиль и выход в выпадающем меню. */
export function AdminAuthActions({
  hideOrganisationsNav = false,
}: {
  hideOrganisationsNav?: boolean;
}) {
  const {
    logout,
    roles,
    organisations,
    user,
    isAuthenticated,
    canManageOrganisations,
  } = useAuth();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useClickOutside(containerRef, () => setOpen(false), open);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  if (!isAuthenticated) return null;

  const roleHint =
    roles.length > 0
      ? roles.join(", ")
      : organisations.map((org) => `${org.name} (${org.role})`).join(", ");

  const showOrganisationsLink =
    !hideOrganisationsNav &&
    (canManageOrganisations ||
      organisations.some((org) => org.role === "org_admin"));

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        title={user?.email ?? "Профиль"}
        aria-label="Меню профиля"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={[
          "inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white font-onest text-sm font-semibold text-slate-700 shadow-sm transition-colors",
          "hover:border-slate-300 hover:bg-slate-50",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30",
        ].join(" ")}
      >
        {profileInitial(user?.email)}
      </button>

      {open ? (
        <div
          className="absolute right-0 top-full z-40 mt-1.5 w-56 rounded-lg border border-slate-200/90 bg-white py-1 shadow-lg"
          role="menu"
        >
          {user?.email || roleHint ? (
            <div className="border-b border-slate-100 px-3 py-2">
              {user?.email ? (
                <p className="truncate font-onest text-sm font-medium text-slate-800">
                  {user.email}
                </p>
              ) : null}
              {roleHint ? (
                <p className="mt-0.5 truncate font-onest text-xs text-slate-500">
                  {roleHint}
                </p>
              ) : null}
            </div>
          ) : null}

          {showOrganisationsLink ? (
            <Link
              to="/admin/organisations"
              role="menuitem"
              className="flex items-center gap-2 px-3 py-2 font-onest text-sm text-slate-700 hover:bg-slate-50"
              onClick={() => setOpen(false)}
            >
              Организации
            </Link>
          ) : null}

          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center px-3 py-2 text-left font-onest text-sm text-slate-700 hover:bg-slate-50"
            onClick={() => {
              setOpen(false);
              void logout();
            }}
          >
            Выйти
          </button>
        </div>
      ) : null}
    </div>
  );
}
